// @spec AST-STR-008
// @spec AST-STR-004
// @spec AST-STR-017
// @spec AST-STR-018
// @spec AST-STR-026
// @spec AST-STR-005
// @spec SUP-STR-041
// @spec VUL-STR-019
// @spec BIA-STR-041
// @spec BCP-STR-049
// @spec AST-STR-027
// @spec AST-STR-055
'use client';

import { createFileRoute, Link, useNavigate, useParams } from '@tanstack/react-router';
import { AlertCircle, ArrowLeft, ChevronRight, RefreshCw } from 'lucide-react';
import * as React from 'react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AIAssetAssistant } from '@/features/assets/components/AIAssetAssistant';
import { ApplyControlSheet } from '@/features/assets/components/ApplyControlSheet';
import { AssetChangeHistorySection } from '@/features/assets/components/AssetChangeHistorySection';
import { AssetControlsSection } from '@/features/assets/components/AssetControlsSection';
import { AssetDetailCustomFields } from '@/features/assets/components/AssetDetailCustomFields';
import { AssetDetailHeader } from '@/features/assets/components/AssetDetailHeader';
import { AssetDetailMetadata } from '@/features/assets/components/AssetDetailMetadata';
import { AssetDetailSkeleton } from '@/features/assets/components/AssetDetailSkeleton';
import { AssetLinkedRisksSection } from '@/features/assets/components/AssetLinkedRisksSection';
import { AssetLinkedSuppliersSection } from '@/features/assets/components/AssetLinkedSuppliersSection';
import { AssetLinkedThreatsSection } from '@/features/assets/components/AssetLinkedThreatsSection';
import { AssetLinkedVulnerabilitiesSection } from '@/features/assets/components/AssetLinkedVulnerabilitiesSection';
import { DecommissionAssetDialog } from '@/features/assets/components/DecommissionAssetDialog';
import { EditAssetDialog } from '@/features/assets/components/EditAssetDialog';
import { useAsset } from '@/features/assets/hooks/useAssets';
import { AssetBCPCoverageSection } from '@/features/bcp/components/AssetBCPCoverageSection';
import { AssetGapsSection } from '@/features/gaps';
import { NIS2MeasureLinkSection } from '@/features/nis2/components/NIS2MeasureLinkSection';
import { useOrganizations } from '@/hooks/use-organizations';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/assets/$assetId')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.assetDetails'),
  }),
  component: AssetDetailPage,
});

/**
 * Asset detail page displaying complete asset information.
 */
function AssetDetailPage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { assetId } = useParams({ from: '/_authenticated/assets/$assetId' });
  const { organizations, isLoading: orgsLoading } = useOrganizations();
  const [isEditOpen, setIsEditOpen] = React.useState(false);
  const [isApplyControlOpen, setIsApplyControlOpen] = React.useState(false);
  const [isDecommissionOpen, setIsDecommissionOpen] = React.useState(false);

  const activeOrg = organizations.find((org) => org.isActive);

  const {
    data: asset,
    isLoading: assetLoading,
    isError,
    error,
    refetch,
  } = useAsset({
    orgId: activeOrg?.id ?? '',
    assetId,
    enabled: Boolean(activeOrg),
  });

  // Loading state while checking organizations
  if (orgsLoading) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-6xl">
          <AssetDetailSkeleton />
        </div>
      </div>
    );
  }

  // No active organization
  if (!activeOrg) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-6xl">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t('common:routeErrors.noOrgSelected')}</AlertTitle>
            <AlertDescription>
              {t('common:routeErrors.noOrgSelectedDesc')}
            </AlertDescription>
          </Alert>
          <Button
            variant="outline"
            onClick={() => void navigate({ to: '/settings/organizations' })}
            className="mt-4"
          >
            {t('common:routeErrors.goToOrganizations')}
          </Button>
        </div>
      </div>
    );
  }

  // Loading state
  if (assetLoading) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-6xl">
          <AssetDetailSkeleton />
        </div>
      </div>
    );
  }

  // Error state
  if (isError) {
    const is404 = (error as { status?: number }).status === 404;
    const is403 = (error as { status?: number }).status === 403;

    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-6xl space-y-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{is404 ? t('common:routeErrors.assetNotFound') : is403 ? t('common:routeErrors.accessDenied') : t('common:routeErrors.error')}</AlertTitle>
            <AlertDescription>
              {is404
                ? 'The asset you are looking for does not exist or has been deleted.'
                : is403
                  ? 'You do not have permission to view this asset.'
                  : 'An error occurred while loading the asset details.'}
            </AlertDescription>
          </Alert>
          <div className="flex gap-2">
            <Button variant="outline" asChild>
              <Link to="/assets">
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t('common:routeErrors.backToAssets')}
              </Link>
            </Button>
            {!is404 && !is403 && (
              <Button variant="outline" onClick={() => void refetch()}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Retry
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // No asset data
  if (!asset) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-6xl space-y-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t('common:routeErrors.assetNotFound')}</AlertTitle>
            <AlertDescription>The asset you are looking for does not exist.</AlertDescription>
          </Alert>
          <Button variant="outline" asChild>
            <Link to="/assets">
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t('common:routeErrors.backToAssets')}
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-6xl space-y-6">
        {/* Breadcrumb */}
        <nav
          className="flex items-center gap-1 text-sm text-muted-foreground"
          aria-label="Breadcrumb"
        >
          <Link to="/assets" className="hover:text-foreground transition-colors">
            Assets
          </Link>
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
          <span className="text-foreground font-medium">{asset.name}</span>
        </nav>

        {/* Back Button */}
        <Button variant="ghost" size="sm" asChild>
          <Link to="/assets">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Assets
          </Link>
        </Button>

        {/* Header Section */}
        <AssetDetailHeader
          asset={asset}
          onEditClick={() => {
            setIsEditOpen(true);
          }}
          onApplyControlClick={() => {
            setIsApplyControlOpen(true);
          }}
          onDecommissionClick={() => {
            setIsDecommissionOpen(true);
          }}
        />

        {/* AI Asset Assistant */}
        <AIAssetAssistant assetId={assetId} orgId={activeOrg.id} asset={asset} />

        {/* Edit Asset Dialog */}
        <EditAssetDialog
          asset={asset}
          open={isEditOpen}
          onOpenChange={setIsEditOpen}
          orgId={activeOrg.id}
        />

        {/* Apply Control Sheet */}
        <ApplyControlSheet
          assetId={assetId}
          assetName={asset.name}
          orgId={activeOrg.id}
          open={isApplyControlOpen}
          onOpenChange={setIsApplyControlOpen}
        />

        {/* Decommission Asset Dialog */}
        <DecommissionAssetDialog
          assetId={assetId}
          assetName={asset.name}
          assetCriticality={asset.criticality}
          isOpen={isDecommissionOpen}
          onOpenChange={setIsDecommissionOpen}
          onDecommissioned={() => {
            void refetch();
          }}
        />

        {/* Metadata Section */}
        <AssetDetailMetadata asset={asset} />

        {/* Controls Section */}
        <AssetControlsSection assetId={assetId} orgId={activeOrg.id} />

        {/* Control Gaps Section */}
        <AssetGapsSection assetId={assetId} orgId={activeOrg.id} />

        {/* Linked Risks Section */}
        <AssetLinkedRisksSection assetId={assetId} orgId={activeOrg.id} />

        {/* Linked Vulnerabilities Section */}
        <AssetLinkedVulnerabilitiesSection assetId={assetId} orgId={activeOrg.id} />

        {/* Linked Threats Section */}
        <AssetLinkedThreatsSection assetId={assetId} orgId={activeOrg.id} />

        {/* Linked Suppliers Section */}
        <AssetLinkedSuppliersSection assetId={assetId} orgId={activeOrg.id} />

        {/* BIA Context Section */}

        {/* BCP Coverage Section */}
        <AssetBCPCoverageSection assetId={assetId} orgId={activeOrg.id} />

        {/* NIS2 Measure Links */}
        <NIS2MeasureLinkSection
          orgId={activeOrg.id}
          sourceEntityId={assetId}
          sourceDomain="AST"
          sourceEntityType="asset"
          sourceEntityTitle={asset.name}
          suggestedMeasures={['j']}
        />

        {/* Change History Section (AST-STR-055) */}
        <AssetChangeHistorySection assetId={assetId} assetName={asset.name} />

        {/* Custom Fields Section (only if has fields) */}
        <AssetDetailCustomFields
          customFields={asset.customFields}
          orgId={activeOrg.id}
          category={asset.category}
        />
      </div>
    </div>
  );
}
